import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useStoreDetail } from '../hooks/useStoreDetail'
import { addToCart, createCheckout, payOrder, likeListing, unlikeListing, saveListing, unsaveListing } from '../api/store'
import { getToken } from '../lib/auth'
import { useMe } from '../hooks/useMe'
import { useReviews } from '../hooks/useReviews'
import { createReview } from '../api/reviews'
import NavBar from '../components/layout/NavBar'
import ReportModal from '../components/moderation/ReportModal'
import AppealModal from '../components/moderation/AppealModal'
import { listMyAppeals, type AppealItem } from '../api/moderation'
import SensitiveMedia from '../components/content/SensitiveMedia'

export default function StoreDetail() {
  const { listingId } = useParams()
  const navigate = useNavigate()
  const { data, loading, error } = useStoreDetail(listingId)
  const title = data?.product?.title || 'Product'
  const subtitle = data?.product?.subtitle || ''
  const gallery = (data?.images && data.images.length > 0)
    ? data.images.map((im) => im.public_url!).filter(Boolean)
    : (data?.product?.thumbnail_media?.public_url ? [data.product.thumbnail_media.public_url] : [])
  const [activeIdx, setActiveIdx] = useState(0)
  const cover = gallery[activeIdx]
  const contentWarning = data?.content_warning || data?.images?.[activeIdx]?.content_warning || data?.product?.thumbnail_media?.content_warning || null
  const available = Math.max(0, (data?.stock_qty ?? 0) - (data?.reserved_qty ?? 0))
  const inStock = available > 0
  const { reviews, aggregate } = useReviews(data?.review_subject_id)
  const [reviewsLocal, setReviewsLocal] = useState(reviews)
  const [qty, setQty] = useState(1)
  const [liked, setLiked] = useState<boolean>(Boolean(data?.liked))
  const [likes, setLikes] = useState<number>(data?.likes_count ?? 0)
  const [saved, setSaved] = useState<boolean>(Boolean(data?.is_saved))
  const [saves, setSaves] = useState<number>(data?.saves_count ?? 0)
  const [lightbox, setLightbox] = useState<{ open: boolean; idx: number }>({ open: false, idx: 0 })
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const [reportOpen, setReportOpen] = useState(false)
  const [appealOpen, setAppealOpen] = useState(false)
  const [appealStatus, setAppealStatus] = useState<AppealItem | null>(null)
  const [appealsLoading, setAppealsLoading] = useState(false)
  // header menu handled by shared NavBar
  const tapRef = useRef<{ x: number; y: number; moved: boolean; t: number } | null>(null)
  const isMobile = () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false)
  const tapBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/store')
  }, [navigate])

  useEffect(() => {
    setLiked(Boolean(data?.liked))
    setLikes(data?.likes_count ?? 0)
    setSaved(Boolean(data?.is_saved))
    setSaves(data?.saves_count ?? 0)
  }, [data?.liked, data?.likes_count, data?.is_saved, data?.saves_count])

  useEffect(() => {
    setReviewsLocal(reviews ?? null)
  }, [reviews])

  async function onAddToCart() {
    if (!listingId) return
    if (!authed) { alert('Please log in to add to cart'); return }
    try {
      await addToCart(listingId, Math.max(1, Math.min(qty, available || qty)))
      alert('Added to cart')
    } catch (e: any) {
      alert(e?.message || 'Failed to add to cart')
    }
  }

  async function onBuyNow() {
    if (!listingId) return
    if (!authed) { alert('Please log in to check out'); return }
    try {
      const idk = `${Date.now()}-${Math.random().toString(36).slice(2)}`
      const order = await createCheckout(listingId, idk)
      const res = await payOrder(order.public_id)
      if (res.ok) alert('Payment complete!')
      else alert('Payment failed')
    } catch (e: any) {
      alert(e?.message || 'Checkout failed')
    }
  }

  async function onToggleLike() {
    if (!listingId) return
    if (!authed) { alert('Please log in to like'); return }
    try {
      if (liked) {
        setLiked(false); setLikes((n) => Math.max(0, n - 1))
        const r = await unlikeListing(listingId)
        setLiked(r.liked); setLikes(r.likes)
      } else {
        setLiked(true); setLikes((n) => n + 1)
        const r = await likeListing(listingId)
        setLiked(r.liked); setLikes(r.likes)
      }
    } catch (e) {
      // ignore
    }
  }

  async function onToggleSave() {
    if (!listingId) return
    if (!authed) { alert('Please log in to save'); return }
    try {
      if (saved) {
        setSaved(false); setSaves((n) => Math.max(0, n - 1))
        const r = await unsaveListing(listingId)
        setSaved(r.saved); setSaves(r.saves)
      } else {
        setSaved(true); setSaves((n) => n + 1)
        const r = await saveListing(listingId)
        setSaved(r.saved); setSaves(r.saves)
      }
    } catch (e) {
      // ignore
    }
  }

  const sellerHandle = data?.seller?.handle?.toLowerCase()
  const myHandle = me?.profile?.handle?.toLowerCase()
  const isSeller = Boolean(sellerHandle && myHandle && sellerHandle === myHandle)
  const moderationState = data?.moderation_state || 'ok'
  const showModerationBanner = isSeller && moderationState !== 'ok'
  const moderationMessage =
    moderationState === 'pending_review'
      ? 'This listing is hidden from public while we review it.'
      : moderationState === 'requires_edit'
        ? 'This listing needs changes before it can be published.'
        : moderationState === 'removed'
          ? 'This listing was removed.'
          : (data?.user_safe_message || '')
  const canAppeal = Boolean(isSeller && moderationState !== 'ok' && data?.last_moderation_event_id)
  const appealOpenState = appealStatus?.status === 'open'

  useEffect(() => {
    if (!authed || !isSeller || !data?.last_moderation_event_id) {
      setAppealStatus(null)
      return
    }
    let cancelled = false
    setAppealsLoading(true)
    listMyAppeals()
      .then(({ items }) => {
        if (cancelled) return
        const match = items.find((item) => item.event_id === data.last_moderation_event_id) || null
        setAppealStatus(match)
      })
      .catch(() => {
        if (!cancelled) setAppealStatus(null)
      })
      .finally(() => {
        if (!cancelled) setAppealsLoading(false)
      })
    return () => { cancelled = true }
  }, [authed, isSeller, data?.last_moderation_event_id])

  return (
    <div
      className="min-h-screen"
      onPointerDown={(e) => {
        if (!isMobile()) return
        const t = e.target as HTMLElement
        if (t?.closest?.('[data-no-tapback="1"]')) return
        if (t?.closest?.('a,button,input,textarea,select,label,[role="button"],.copy-code-btn')) return
        tapRef.current = { x: e.clientX, y: e.clientY, moved: false, t: Date.now() }
      }}
      onPointerMove={(e) => {
        const s = tapRef.current
        if (!s) return
        const dx = Math.abs(e.clientX - s.x)
        const dy = Math.abs(e.clientY - s.y)
        if (dx > 12 || dy > 12) s.moved = true
      }}
      onPointerUp={(e) => {
        if (!isMobile()) return
        const s = tapRef.current
        tapRef.current = null
        if (!s) return
        const dt = Date.now() - s.t
        if (s.moved || dt > 650) return
        const t = e.target as HTMLElement
        if (t?.closest?.('[data-no-tapback="1"]')) return
        if (t?.closest?.('a,button,input,textarea,select,label,[role="button"],.copy-code-btn')) return
        const sel = window.getSelection?.()
        if (sel && sel.toString().trim()) return
        tapBack()
      }}
    >
      <NavBar showSearch={false} showMobileModePill={false} />

      {error && <div className="mx-auto max-w-3xl px-4 py-6 text-red-400">{error}</div>}
      {loading && !data && <div className="mx-auto max-w-3xl px-4 py-6 glass rounded-xl h-64" />}

      {data && (
        <main className="mx-auto max-w-6xl px-4 py-6">
          {showModerationBanner && (
            <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              moderationState === 'requires_edit'
                ? 'border-red-400/40 bg-red-500/10 text-red-100'
                : moderationState === 'removed'
                  ? 'border-white/20 bg-white/5 text-white/70'
                  : 'border-amber-400/30 bg-amber-500/10 text-amber-100'
            }`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>{moderationMessage}</div>
                <div className="flex items-center gap-2">
                  {moderationState === 'requires_edit' && (
                    <Link to={`/store/list/new?listing=${data.public_id}`} className="rounded-lg bg-red-500 px-3 py-1 text-xs font-semibold text-white hover:bg-red-400">
                      Fix now
                    </Link>
                  )}
                  {canAppeal && !appealOpenState && (
                    <button
                      type="button"
                      onClick={() => setAppealOpen(true)}
                      className="rounded-lg border border-white/20 px-3 py-1 text-xs font-semibold text-white/90 hover:bg-white/10"
                      disabled={appealsLoading}
                    >
                      Appeal
                    </button>
                  )}
                  {appealOpenState && (
                    <span className="rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">Appeal submitted</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {appealStatus && appealStatus.status !== 'open' && (
            <div className="mb-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
              <div className="font-semibold">
                {appealStatus.status === 'accepted' ? 'Appeal accepted.' : 'Appeal denied.'}
              </div>
              {appealStatus.user_note && <div className="mt-1 text-white/70">{appealStatus.user_note}</div>}
            </div>
          )}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) navigate(-1)
                else navigate('/store')
              }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/85 hover:bg-white/10"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
              Back to Store
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left: Gallery & details */}
            <div className="lg:col-span-7">
              {/* Title under gallery on mobile */}
              <div className="mb-4 lg:hidden">
                <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
                {subtitle && <div className="text-white/85">{subtitle}</div>}
              </div>
              {/* Main image */}
              <div className="relative rounded-xl overflow-hidden">
                <div className="relative aspect-[16/9] bg-white/5">
                  {cover ? (
                    <button
                      type="button"
                      onClick={() => setLightbox({ open: true, idx: activeIdx })}
                      className="absolute inset-0 h-full w-full"
                    >
                      <SensitiveMedia imageUrl={cover} warning={contentWarning as any} alt={title} className="h-full w-full" />
                    </button>
                  ) : null}
                </div>
              </div>
              {/* Thumbnails */}
              {gallery.length > 1 && (
                <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {gallery.map((src, i) => (
                    <button key={i} onClick={() => setActiveIdx(i)} className={`relative rounded-lg overflow-hidden aspect-square border ${i===activeIdx ? 'border-brand' : 'border-white/10'}`}>
                      <img src={src} className="absolute inset-0 h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

            {/* Description */}
            {data?.product?.description && (
              <section className="mt-8">
                <h3 className="text-lg font-extrabold mb-2">Description</h3>
                <p className="text-white/85 whitespace-pre-wrap">{data.product.description}</p>
              </section>
            )}

            {/* Seller block (below description) */}
            {data?.seller?.handle && (
              <section className="mt-6">
                <h3 className="text-lg font-extrabold mb-2">Sold by</h3>
                <Link to={data?.seller?.public_id ? `/people/i/${data.seller.public_id}` : '/people'} className="flex items-center gap-3 group">
                  {data.seller?.avatar ? (
                    <img src={data.seller.avatar} className="h-10 w-10 rounded-full object-cover border border-white/15" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-white/10" />
                  )}
                  <div className="text-sm font-semibold group-hover:underline">@{data.seller.handle}</div>
                  {/* Optional stars placeholder for future seller reviews */}
                  {/* <div className="ml-auto text-sm text-white/80">
                    <span className="text-yellow-400">★</span> 4.6 <span className="text-white/60">/ 5</span>
                  </div> */}
                </Link>
              </section>
            )}

            {/* Extra gallery grid */}
            {data?.images?.length > 1 && (
              <section className="mt-8">
                <h3 className="text-lg font-extrabold mb-2">Gallery</h3>
                <div className="grid grid-cols-2 gap-2">
                    {data.images.slice(1, 5).map((im, i) => (
                      <img key={i} src={im.public_url} className="w-full h-40 object-cover rounded-lg" />
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Right: Sticky purchase panel */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-24">
                <div className="glass rounded-2xl p-5">
                  <h1 className="hidden lg:block text-2xl font-extrabold tracking-tight">{title}</h1>
                  {subtitle && <div className="hidden lg:block text-white/85 mt-1">{subtitle}</div>}

                  <div className="mt-4 text-3xl font-extrabold">{data.price_pixe ? fmtPixe(data.price_pixe) : ''}</div>
                  <div className="mt-2 text-sm text-white/80">
                    {data.condition ? (
                      <span className="mr-2 inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs font-semibold">
                        {(data.condition || '').toString().toUpperCase()}
                      </span>
                    ) : null}
                    {data.seller?.handle ? (
                      <span>Sold by @{data.seller.handle}</span>
                    ) : null}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className={`text-sm ${inStock ? 'text-emerald-400' : 'text-red-400'}`}>
                      {inStock ? `In stock (${available})` : 'Out of stock'}
                    </span>
                    <div className="flex items-center gap-3 text-sm">
                      <button onClick={onToggleLike} className={`px-2.5 py-1 rounded-lg border ${liked ? 'border-brand bg-brand/20 text-white' : 'border-white/15 hover:bg-white/5'}`}>❤ {likes}</button>
                      <button onClick={onToggleSave} className={`px-2.5 py-1 rounded-lg border ${saved ? 'border-brand bg-brand/20 text-white' : 'border-white/15 hover:bg-white/5'}`}>Save {saves}</button>
                      <button onClick={() => { navigator.clipboard?.writeText(location.href); }} className="px-2.5 py-1 rounded-lg border border-white/15 hover:bg-white/5">Share</button>
                      <button onClick={() => setReportOpen(true)} className="px-2.5 py-1 rounded-lg border border-white/15 hover:bg-white/5">Report</button>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className="inline-flex items-center rounded-lg border border-white/15">
                      <button onClick={() => setQty((q) => Math.max(1, q-1))} className="px-3 py-2">-</button>
                      <input value={qty} onChange={(e)=> setQty(Math.max(1, parseInt(e.target.value || '1')||1))} className="w-12 bg-transparent text-center outline-none" />
                      <button onClick={() => setQty((q) => Math.max(1, Math.min((available||99), q+1)))} className="px-3 py-2">+</button>
                    </div>
                    <button onClick={onAddToCart} disabled={!inStock} className={`flex-1 inline-flex items-center justify-center rounded-lg border px-4 py-2 font-semibold ${inStock ? 'border-white/15 hover:bg-white/5' : 'opacity-50 cursor-not-allowed border-white/10'}`}>Add to cart</button>
                  </div>
                  <button onClick={onBuyNow} disabled={!inStock} className={`mt-3 w-full inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold text-white ${inStock ? 'bg-brand hover:bg-brand-600' : 'bg-white/10 cursor-not-allowed'}`}>Buy now</button>
                </div>
              </div>
            </aside>
          </div>

          {/* Meta */}
          {/* (Removed meta cards per request) */}

          {/* Reviews */}
          <section className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-extrabold">Reviews</h3>
              {aggregate ? (
                <div className="flex items-center gap-4 text-sm text-white/80">
                  <span className="inline-flex items-center gap-1">
                    <span className="text-yellow-400">★</span>
                    <span className="font-semibold">{aggregate.bayesian_avg?.toFixed?.(2) ?? aggregate.mean_raw?.toFixed?.(2) ?? '0.0'}</span>
                    <span className="text-white/60">/ 5</span>
                    <span className="ml-2 text-white/60">{aggregate.count_total} ratings</span>
                  </span>
                  <button
                    onClick={() => { if (!authed) { navigate('/login'); return } setShowForm(true) }}
                    className="inline-flex items-center rounded-full border border-white/15 px-3 py-1.5 font-semibold hover:bg-white/5"
                  >
                    Write a review
                  </button>
                </div>
              ) : null}
            </div>
            {!reviewsLocal && <div className="h-5" />}
            {reviewsLocal && reviewsLocal.length === 0 && (
              <div className="text-white/70 text-sm">No reviews yet.</div>
            )}
            {reviewsLocal && reviewsLocal.length > 0 && (
              <ul>
                {reviewsLocal.slice(0, 8).map((r) => (
                  <li key={r.id} className="py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      {r.author_profile?.avatar_url ? (
                        <img src={r.author_profile.avatar_url} className="h-8 w-8 rounded-full" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-white/10" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <span>{r.author_profile?.display_name || r.author_profile?.handle || 'User'}</span>
                          <span className="text-white/60">•</span>
                          <span className="text-yellow-400">{'★'.repeat(Math.max(1, Math.round(r.overall_rating || 0)))}</span>
                          <span className="text-white/60">{r.overall_rating}/5</span>
                        </div>
                        {r.body && <div className="text-sm text-white/85 mt-1">{r.body}</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Inline write-a-review form */}
            {aggregate && (
              <WriteReview
                authed={authed}
                onLogin={() => navigate('/login')}
                subjectId={data?.review_subject_id}
                onCreated={(rv) => setReviewsLocal((prev) => [rv, ...(prev ?? [])])}
              />
            )}
          </section>
        </main>
      )}

      {/* Lightbox */}
      {lightbox.open && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightbox({ open: false, idx: 0 })}>
          <button className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20" aria-label="Close" onClick={() => setLightbox({ open: false, idx: 0 })}>
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-white mx-auto my-1.5"><path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
          <div className="max-w-5xl w-full px-4">
            <img src={gallery[lightbox.idx] || cover} className="w-full h-auto object-contain" />
          </div>
        </div>
      )}
      {data && (
        <ReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          contentType="listing"
          contentId={data.public_id}
          title={title ? `Report "${title}"` : 'Report listing'}
        />
      )}
      {data && (
        <AppealModal
          open={appealOpen}
          onClose={() => setAppealOpen(false)}
          onSuccess={() => setAppealStatus({ appeal_id: '', event_id: data.last_moderation_event_id, content_type: 'listing', content_id: '', created_at: new Date().toISOString(), status: 'open' })}
          contentType="listing"
          contentId={data.public_id}
          title={title ? `Appeal "${title}"` : 'Appeal listing'}
        />
      )}
    </div>
  )
}

function fmtPixe(p?: string | number | null): string {
  const n = typeof p === 'number' ? p : parseFloat(String(p ?? 0))
  if (!isFinite(n)) return '0.00 PIXE'
  return `${n.toFixed(2)} PIXE`
}

function WriteReview({ authed, onLogin, subjectId, onCreated }: { authed: boolean; onLogin: () => void; subjectId?: number; onCreated: (r: any) => void }) {
  const [showForm, setShowForm] = useState(false)
  const [rating, setRating] = useState(5)
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!subjectId) return null

  async function submit() {
    if (!authed) { onLogin(); return }
    if (rating < 1) return
    setSubmitting(true)
    try {
      const rv = await createReview({ subject: subjectId, overall_rating: rating, body: body.trim() || undefined })
      setBody('')
      setRating(5)
      setShowForm(false)
      onCreated(rv)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mt-6">
      {!showForm ? (
        <button onClick={() => (authed ? setShowForm(true) : onLogin())} className="inline-flex items-center rounded-full border border-white/15 px-3 py-1.5 font-semibold hover:bg-white/5">Write a review</button>
      ) : (
        <div className="rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-white/80 mr-2">Your rating:</span>
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setRating(n)} className={`h-8 w-8 rounded-md ${rating>=n ? 'text-yellow-400' : 'text-white/30'}`}>★</button>
            ))}
          </div>
          <textarea value={body} onChange={(e)=>setBody(e.target.value)} placeholder="Share your experience" className="mt-3 w-full rounded-lg bg-white/5 border border-white/10 p-3 outline-none focus:ring-2 focus:ring-brand min-h-24" />
          <div className="mt-3 flex items-center gap-2">
            <button disabled={submitting} onClick={submit} className="inline-flex items-center rounded-lg bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-600 disabled:opacity-60">Submit</button>
            <button disabled={submitting} onClick={()=>setShowForm(false)} className="inline-flex items-center rounded-lg border border-white/15 px-4 py-2 font-semibold hover:bg-white/5">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
